import { FC, useEffect } from 'react';
import ReactDOM from 'react-dom';
import tdk from '@/constants/tdk';
import { OpenType } from '@/constants';
import styles from './index.module.scss';
import { IUserLoginInfo } from '@/api/user';
import { useTranslations } from 'next-intl';
import { IState, namespace } from '@/context';
import { GetServerSidePropsContext } from 'next';
import ConsultCard from './components/ConsultCard';
import { getNavsList, ITopLevelNav } from '@/api/nav';
import TabSearchComponent from '@/components/TabSearch';
import useTabSearchHook from '../home/hooks/useTabSearchHook';
import getThreadList, { IThreadLisItem } from '@/api/thread/list';
import { getDefaultChannelActiveKeys } from '@/hooks/useChannelDeviceTab';
import Carousel, { AttachType, CarouselItem } from '@/components/Carousel';
import { getChannelDetail, getSubChannelTree, ISubChannelTreeRes, TopLevelChannels } from '@/api/home';
import CommonHead from '@/components/CommonHead';
import useBodyContainer from '@/hooks/useBodyContainer';


interface Error {
  message: string;
  code: string | number;
};

interface IActivityProps {
  ua: string;
  error?: Error[];
  account?: IUserLoginInfo;
  sources: ISubChannelTreeRes;
  defaultChannelActiveSlug: string;
  defaultChannelActiveSort: string;
  channelList: ISubChannelTreeRes[];
  firstPageArticalList: IState[namespace.activity]['articalList'][string]['data'];
}

const Consult: FC<IActivityProps> = (props) => {
  const container = useBodyContainer();

  const { error, channelList, firstPageArticalList, sources } = props;
  const t = useTranslations();
  const {tabs, moreTabs, tabSortFilterMap} = useTabSearchHook(channelList);

  const bannerList: CarouselItem[] = (sources?.channel_config?.banner_config || []).map(item => {
    const { link_url, pc_banner_url, video_play_info, open_type = OpenType.CurrentWindow } = item;
    const hasVideo = video_play_info?.video_uid;
  
    return {
      alt: '',
      link: {
        openType: open_type,
        src: link_url,
      },
      type: hasVideo ? AttachType.Video : AttachType.Image,
      src: hasVideo ? video_play_info.definitions[1].src : pc_banner_url,
      ...(hasVideo && { fallbackSrc: pc_banner_url }),
    };
  });

  return (
    <div
      className={styles.consultMainContent}
      style={{marginTop: !bannerList.length ? '0' : '16px'}}
    >
      <CommonHead {...tdk[namespace.consult]} />
      {container &&
        ReactDOM.createPortal(
        '咨询 - 大疆社区',
        container
      )}
      {
        !!bannerList.length && (
          <div className={styles.bannerContent}>
            <Carousel
              items={bannerList}
              width={640}
              height={120}
            />
          </div>
        )
      }
      {/* tab 内容搜索组件 */}
      <div className={styles.mainContent}>
        <TabSearchComponent<IThreadLisItem>
          channel={TopLevelChannels.home_page_consult_channel}
          id="tid"
          tabs={tabs}
          pageSize={10}
          moreTabs={moreTabs}
          Component={ConsultCard} // 内容渲染组件
          tabSortFilterMap={tabSortFilterMap}
          defaultContent={firstPageArticalList} // 默认展示首屏内容
          fetchApi={({ tabKey, sortKey, offset = 10, pageSize }) => getThreadList({   // 获取数据api
            offset,
            limit: pageSize,
            sort_type: sortKey,
            channel_slug: tabKey,
          })}
        />
      </div>
    </div>);
};

const handlePromiseResult = (result:  PromiseSettledResult<any>, setValue: ((value: any) => void), errorList: Error[]) => {
  if (result?.status === 'fulfilled') {
    setValue(result.value || []);
  } else {
    errorList.push(result.reason);
  }
};

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const ua = ctx.req.headers['user-agent'] || '';

  let navList: ITopLevelNav[] = [];
  let firstPageArticalList: IThreadLisItem[] = [];
  let channelList: ISubChannelTreeRes[] = [];
  let defaultValues = {
    defaultChannelActiveSort: '',
    defaultChannelActiveSlug: '',
  };

  let sources: ISubChannelTreeRes | [] = {} as unknown as ISubChannelTreeRes;

  let error: Error[] = [];
  const headers = ctx.req ? ctx.req.headers : {};

  try {
    channelList =
    (
      await getSubChannelTree(
        {
          slug: TopLevelChannels.home_page_consult_channel,
          level_num: 2,
        },
        true,
        headers,
      )
    )?.sub_channel_info_dtos || [];

    const { defaultChannelActiveKeys, defaultChannelActiveSort } = getDefaultChannelActiveKeys(channelList);
    defaultValues = {
      defaultChannelActiveSort,
      defaultChannelActiveSlug: defaultChannelActiveKeys?.[defaultChannelActiveKeys.length - 1],
    };

    const [artical, navs, cmsSources ] = await Promise.allSettled([
      getThreadList(
        {
          channel_slug: defaultValues?.defaultChannelActiveSlug,
          sort_type: defaultValues.defaultChannelActiveSort,
        },
        true,
        headers,
      ),
      getNavsList(true),
      getChannelDetail(
        {
          slug: TopLevelChannels.home_page_consult_channel,
        },
        true,
        headers,
      ),
    ]);

    handlePromiseResult(navs, (value: typeof navList) => (navList = value), error);
    handlePromiseResult(cmsSources, (value: typeof sources) => (sources = value), error);
    handlePromiseResult(artical, (value: typeof firstPageArticalList) => (firstPageArticalList = value), error);

    // 兜底：首屏数据为空时，尝试请求 offset=10（后端 offset=0 可能返回空但 offset=10 有数据）
    if (!firstPageArticalList?.length) {
      try {
        firstPageArticalList = await getThreadList(
          {
            channel_slug: defaultValues?.defaultChannelActiveSlug,
            sort_type: defaultValues.defaultChannelActiveSort,
            offset: 10,
          },
          true,
          headers,
        );
      } catch (e) {}
    }
  } catch (e) {
  }

  return {
    props: {
      ua,
      error,
      navList,
      sources,
      channelList,
      firstPageArticalList,
      defaultChannelActiveSlug: defaultValues?.defaultChannelActiveSlug || null,
      defaultChannelActiveSort: defaultValues?.defaultChannelActiveSort || null,
    },
  };
}

export default Consult;
